import { Box, keyframes } from '@mui/material';
import * as React from 'react';
import { useSpotlight } from '../../stores/useSpotlight';
import { BLURPLE } from './animations';

// Convert hex to RGB for CSS variable
const hexToRgb = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : '25, 118, 210'; // fallback to MUI primary
};

// Pre-compute BLURPLE RGB value since it's a constant
const BLURPLE_RGB = hexToRgb(BLURPLE);

// Pulsing ring animation
const pulseRing = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(var(--pulse-color), 0.7);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(var(--pulse-color), 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--pulse-color), 0);
  }
`;

interface SpotlightPulseProps {
  /** Unique identifier for this spotlight target */
  id: string;
  /** The element to wrap (typically a button) */
  children: React.ReactNode;
  /** Duration of auto-clear in ms (default 3000) */
  autoClearMs?: number;
  /** Whether to expand to fill flex container (adds flex: 1) */
  fullWidth?: boolean;
}

/**
 * Wraps an element to make it pulse when spotlighted.
 * Used by the activation checklist to draw user attention to specific buttons.
 */
export const SpotlightPulse: React.FC<SpotlightPulseProps> = ({
  id,
  children,
  autoClearMs = 3000,
  fullWidth = false,
}) => {
  const { activeSpotlight, clearSpotlight } = useSpotlight();
  const isActive = activeSpotlight === id;
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-clear spotlight after timeout
  React.useEffect(() => {
    if (isActive) {
      timeoutRef.current = setTimeout(() => {
        clearSpotlight();
      }, autoClearMs);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isActive, autoClearMs, clearSpotlight]);

  // Clear spotlight on click
  const handleClick = React.useCallback(() => {
    if (isActive) {
      clearSpotlight();
    }
  }, [isActive, clearSpotlight]);

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'inline-flex',
        position: 'relative',
        borderRadius: 1,
        '--pulse-color': BLURPLE_RGB,
        ...(fullWidth && { flex: 1, width: '100%' }),
        ...(isActive && {
          animation: `${pulseRing} 0.8s ease-out 3`,
          zIndex: 1300,
        }),
      }}
    >
      {children}
    </Box>
  );
};
